import { persistedStorage } from '@platform/design-system';
import { isLocale, type Locale } from '@platform/i18n';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ShellState = {
  readonly sidebarExpanded: boolean;
  readonly locale: Locale | null;
  readonly openModules: readonly string[];
  readonly toggleSidebar: () => void;
  readonly toggleModule: (id: string) => void;
  readonly openModule: (id: string) => void;
  readonly setLocale: (locale: Locale) => void;
};

export const SHELL_KEY = 'admin.shell';

const storedLocale = (value: unknown): Locale | null =>
  typeof value === 'string' && isLocale(value) ? value : null;

const storedModules = (value: unknown): readonly string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];

export const useShell = create<ShellState>()(
  persist(
    (set) => ({
      sidebarExpanded: true,
      locale: null,
      openModules: [],
      toggleModule: (id) => {
        set((state) => ({
          openModules: state.openModules.includes(id)
            ? state.openModules.filter((open) => open !== id)
            : [...state.openModules, id],
        }));
      },
      openModule: (id) => {
        set((state) =>
          state.openModules.includes(id) ? state : { openModules: [...state.openModules, id] },
        );
      },
      toggleSidebar: () => {
        set((state) => ({ sidebarExpanded: !state.sidebarExpanded }));
      },
      setLocale: (locale) => {
        set({ locale });
      },
    }),
    {
      name: SHELL_KEY,
      storage: persistedStorage(),
      partialize: (state) => ({
        sidebarExpanded: state.sidebarExpanded,
        locale: state.locale,
        openModules: state.openModules,
      }),
      merge: (persisted, current) => {
        const stored = persisted as Partial<ShellState> | undefined;
        return {
          ...current,
          sidebarExpanded:
            typeof stored?.sidebarExpanded === 'boolean' ? stored.sidebarExpanded : current.sidebarExpanded,
          locale: storedLocale(stored?.locale),
          openModules: storedModules(stored?.openModules),
        };
      },
    },
  ),
);
